/* eslint-disable sonarjs/public-static-readonly */
import { PropertyOptions } from './decorators/property';
import { QueryOptions } from './decorators/query';
import { StorageOptions } from './decorators/storage';
import { functionValue } from './functionValue';
import { Hook } from './hook';
import { StateEvent } from './state-event';

export type HasChanged = (value: unknown, old: unknown) => boolean;

export type Unsubscribe = () => void;

export type StateEventListener = (evt: StateEvent) => void;

/**
 * Change function that returns true if `value` is different from `oldValue`.
 * This method is used as the default for a property's `hasChanged` function.
 * This ensures (old==NaN, value==NaN) always returns false
 */
// eslint-disable-next-line max-len, no-self-compare
export const notEqual: HasChanged = (value: unknown, old: unknown): boolean => old !== value && (old === old || value === value); // eslint-disable-line 

export type PropertyMapOptions = PropertyOptions &
StorageOptions &
QueryOptions &
{ initialValue: unknown, hook?: { [key: string]: unknown }, resetValue: unknown };

/**
 * Callback function - used as callback subscription to a state change
 */
export type Callback = (key: string, value: unknown, state: State) => void;

/**
 * A state, firing `lit-state-change` when any of it property changes
 *
 */
export class State extends EventTarget {
  // a map holding decorators definition.
  // it set in the @property decorator
  static propertyMap: Map<PropertyKey, PropertyMapOptions>;

  static properties: PropertyOptions;
  static finalized: boolean = false;

  static initPropertyMap() {
    if (!this.propertyMap) {
      this.propertyMap = new Map<string, PropertyMapOptions>();
    }
  }

  get propertyMap() {
    return (this.constructor as typeof State).propertyMap;
  }

  get stateValue() {
    // eslint-disable-next-line max-len
    return Object.fromEntries([...this.propertyMap].map(([key]) => [key, (this as object as { [key: string]: unknown })[key as string]]));
  }

  // hold a reference to hooks
  hookMap: Map<string, Hook> = new Map();

  constructor() {
    super();
    (this.constructor as typeof State).finalize();
    // make sure all getter and setters are called once as some work is
    // being done in @decorator getter and setter. For instance, @storage
    // stores the value to local storage in setter.
    if (this.propertyMap) {
      [...this.propertyMap].forEach(([key, definition]) => {
        if (definition.initialValue !== undefined) {
          const value: unknown = functionValue(definition.initialValue);
          (this as object as { [key: string]: unknown })[key as string] = value;
          // eslint-disable-next-line no-param-reassign
          definition.value = value;
        }
      });
    }
  }

  protected static finalize() {
    if (this.finalized) {
      return false;
    }
    this.finalized = true;
    const propKeys = Object.keys(this.properties || {});
    // This for/of is ok because propKeys is an array
    // eslint-disable-next-line no-restricted-syntax
    for (const p of propKeys) {
      // note, use of `any` is due to TypeScript lack of support for symbol in
      // index types
      // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-argument
      this.createProperty(p, (this.properties)[p]);
    }
    return true;
  }

  static createProperty(
    name: PropertyKey,
    options?: PropertyOptions,
  ) {
    // Note, since this can be called by the `@property` decorator which
    // is called before `finalize`, we ensure finalization has been kicked off.
    this.finalize();
    const key = typeof name === 'symbol' ? Symbol() : `__${name}`; // eslint-disable-line symbol-description
    const descriptor = this.getPropertyDescriptor(String(name), key, options);
    Object.defineProperty(this.prototype, name, descriptor);
  }

  protected static getPropertyDescriptor(
    name: string,
    key: PropertyKey,
    options?: PropertyOptions,
  ): PropertyDescriptor {
    const hasChanged = options?.hasChanged || notEqual;

    return {
      get(): unknown { return (this as object as { [key: string]: unknown })[key as string]; },
      set(this: State, value: unknown) {
        const oldValue = (this as object as { [key: string]: unknown })[
          name
        ];
        (this as object as { [key: string]: unknown })[key as string] = value;
        if (hasChanged(value, oldValue) === true) {
          this.dispatchStateEvent(name, value, this);
        }
      },
      configurable: true,
      enumerable: true,
    };
  }

  /**
   * Reset the state to its original values, skipping
   * properties marked as skipReset
   */
  reset() {
    // reset all hooks first;
    this.hookMap.forEach((hook) => hook.reset());

    [...this.propertyMap]
      .filter(([, definition]) => !(definition.skipReset === true || definition.resetValue === undefined))
      .forEach(([key, definition]) => {
        (this as object as { [key: string]: unknown })[key as string] = definition.resetValue;
      });
  }

  /**
   * subscribe to state change event. The callback will be called anytime
   * a state property change if `nameOrNames` is undefined, or only for matching
   * property values specified by `nameOrNames`
   * @param callback the callback function to call
   * @param nameOrNames
   * @returns a unsubscribe function.
   */
  subscribe(callback: Callback, nameOrNames?: string | string[], options?: AddEventListenerOptions): Unsubscribe {
    if (nameOrNames && !Array.isArray(nameOrNames)) {
      nameOrNames = [nameOrNames]; // eslint-disable-line no-param-reassign
    }

    const cb : StateEventListener = (event: StateEvent) => {
      if (!nameOrNames || (nameOrNames as string[]).includes(event.key)) {
        callback(event.key, event.value, this);
      }
    };

    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    // @ts-expect-error "Works"
    this.addEventListener(StateEvent.eventName, cb, options);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    // @ts-expect-error "Works"
    return () => this.removeEventListener(StateEvent.eventName, cb);
  }

  private dispatchStateEvent(key: string, eventValue: unknown, state: State) {
    this.dispatchEvent(new StateEvent(key, eventValue, state));
  }
}
